import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Check, BookOpen, Headphones, Package } from 'lucide-react';

const Pricing = ({ onPurchaseClick }) => {
  const { t } = useTranslation();
  const digitalFeatures = [
    t('pricing.features.aiWritten'),
    t('pricing.features.formatted'),
    t('pricing.features.instantDownload'),
    t('pricing.features.lifetime'),
    t('pricing.features.edit'),
    t('pricing.features.share'),
  ];

  const bundleFeatures = [
    t('pricing.bundleFeatures.everything'),
    t('pricing.bundleFeatures.hardcover'),
    t('pricing.bundleFeatures.audiobook'),
    t('pricing.bundleFeatures.delivered'),
    t('pricing.bundleFeatures.cover'),
    t('pricing.bundleFeatures.gift'),
  ];

  return (
    <div className="py-24 px-4 bg-gradient-to-br from-purple-50/60 to-pink-50/60" data-testid="pricing-section">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-16">
          {t('pricing.title')}
        </h2>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Digital Plan */}
          <div className="pricing-highlight rounded-3xl p-8 shadow-lg scale-in">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 px-4 py-1.5 rounded-full mb-5 text-sm font-medium">
                <BookOpen className="w-3.5 h-3.5" />
                <span>{t('pricing.digital')}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                {t('pricing.digitalTitle')}
              </h3>
              <div className="flex items-baseline justify-center gap-1 mb-3">
                <span className="text-5xl font-bold text-gray-800">{t('pricing.digitalPrice')}</span>
              </div>
              <p className="text-sm text-gray-500">{t('pricing.oneTime')}</p>
            </div>

            <div className="space-y-2.5 mb-8 stagger-children">
              {digitalFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/60 rounded-xl p-3 fade-in">
                  <div className="bg-green-100 rounded-full p-1 flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              size="lg"
              onClick={onPurchaseClick}
              className="btn-primary w-full text-white py-6 text-base font-semibold rounded-full border-0"
              data-testid="pricing-start-btn"
            >
              {t('pricing.startMyStory')}
            </Button>

            <p className="text-center text-xs text-gray-500 mt-5">
              {t('pricing.instantDelivery')}
            </p>
          </div>

          {/* Bundle Plan */}
          <div className="relative pricing-highlight rounded-3xl p-8 shadow-lg scale-in ring-2 ring-amber-300">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-bold px-5 py-1.5 rounded-full shadow-md">
                {t('pricing.bestValue')}
              </span>
            </div>

            <div className="text-center mb-8 mt-2">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 px-4 py-1.5 rounded-full mb-5 text-sm font-medium">
                <Package className="w-3.5 h-3.5" />
                <span>{t('pricing.bundleTag')}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                {t('pricing.bundleTitle')}
              </h3>
              <div className="flex items-baseline justify-center gap-2 mb-3">
                <span className="text-5xl font-bold text-gray-800">{t('pricing.bundlePrice')}</span>
                <span className="text-sm text-gray-400 line-through">{t('pricing.bundleOldPrice')}</span>
              </div>
              <p className="text-sm text-amber-600 font-medium">{t('pricing.bundleSave')}</p>
            </div>

            <div className="space-y-2.5 mb-8 stagger-children">
              {bundleFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/60 rounded-xl p-3 fade-in">
                  <div className="bg-amber-100 rounded-full p-1 flex-shrink-0">
                    {index === 0 ? <Package className="w-3.5 h-3.5 text-amber-600" /> :
                     index === 2 ? <Headphones className="w-3.5 h-3.5 text-amber-600" /> :
                     <Check className="w-3.5 h-3.5 text-amber-600" />}
                  </div>
                  <span className="text-sm text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              size="lg"
              onClick={onPurchaseClick}
              className="w-full bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white py-6 text-base font-semibold rounded-full border-0 shadow-lg"
              data-testid="pricing-bundle-btn"
            >
              {t('pricing.getBundle')}
            </Button>

            <p className="text-center text-xs text-gray-500 mt-5">
              {t('pricing.bundleDelivery')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
